import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, MessageCircle, ShoppingBag, CreditCard } from "lucide-react";
import { useCart } from "@/shop/CartContext";
import { inr } from "@/shop/products";

const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER || "919003140803";

const CartDrawer = () => {
  const { items, open, setOpen, setQty, removeItem, subtotal, clear } = useCart();
  const navigate = useNavigate();

  const checkout = () => {
    const lines = [
      "Hi Zentra! I'd like to order:",
      ...items.map(
        (i) => `• ${i.qty} × ${i.product.title} (${i.product.short}) — ${inr(i.product.price * i.qty)}`
      ),
      `Subtotal: ${inr(subtotal)}`,
      "Please confirm availability, payment and delivery details.",
    ];
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm"
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[95] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-[#121212]"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <h2 className="flex items-center gap-2 font-display text-2xl uppercase tracking-wide text-white">
                <ShoppingBag className="h-5 w-5 text-[#7ED321]" />
                Your Cart
              </h2>
              <button
                onClick={() => setOpen(false)}
                data-testid="cart-close-button"
                aria-label="Close cart"
                className="flex h-9 w-9 items-center justify-center border border-white/15 text-white/70 transition-colors hover:border-[#7ED321] hover:text-[#7ED321]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <ShoppingBag className="h-10 w-10 text-white/20" />
                  <p className="mt-4 text-sm text-white/50">Your cart is empty.</p>
                  <a
                    href="/shop"
                    onClick={() => setOpen(false)}
                    data-testid="cart-empty-shop-link"
                    className="mt-6 rounded-full bg-[#7ED321] px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors hover:bg-[#8FE331]"
                  >
                    Shop Zentra
                  </a>
                </div>
              ) : (
                <ul className="space-y-4">
                  {items.map((i) => (
                    <li
                      key={i.slug}
                      data-testid={`cart-item-${i.slug}`}
                      className="flex gap-4 rounded-2xl border border-white/10 bg-[#0A0A0A] p-4"
                    >
                      <img
                        src={i.product.image}
                        alt={i.product.title}
                        className="h-16 w-16 shrink-0 rounded-xl border border-white/10 bg-[#0c0e0b] object-contain p-1.5"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-white">{i.product.title}</p>
                        <p className="mt-0.5 text-[11px] text-white/40">{i.product.short}</p>
                        <div className="mt-2.5 flex items-center justify-between">
                          <div className="flex items-center border border-white/15">
                            <button
                              onClick={() => setQty(i.slug, i.qty - 1)}
                              data-testid={`cart-minus-${i.slug}`}
                              aria-label="Decrease quantity"
                              className="flex h-7 w-7 items-center justify-center text-white/70 hover:text-[#7ED321]"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-8 text-center text-xs font-semibold text-white" data-testid={`cart-qty-${i.slug}`}>
                              {i.qty}
                            </span>
                            <button
                              onClick={() => setQty(i.slug, i.qty + 1)}
                              data-testid={`cart-plus-${i.slug}`}
                              aria-label="Increase quantity"
                              className="flex h-7 w-7 items-center justify-center text-white/70 hover:text-[#7ED321]"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="text-sm font-semibold text-[#7ED321]">
                            {inr(i.product.price * i.qty)}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(i.slug)}
                        data-testid={`cart-remove-${i.slug}`}
                        aria-label="Remove item"
                        className="self-start text-white/30 transition-colors hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-6 py-5">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.18em] text-white/50">Subtotal</span>
                  <span className="font-display text-2xl text-white" data-testid="cart-subtotal">
                    {inr(subtotal)}
                  </span>
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-white/40">
                  Delivery and payment (UPI / COD) confirmed on WhatsApp.
                </p>
                <button
                  onClick={() => { setOpen(false); navigate("/checkout"); }}
                  data-testid="cart-checkout-button"
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#7ED321] py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors hover:bg-[#8FE331]"
                >
                  <CreditCard className="h-4 w-4" />
                  Checkout
                </button>
                <button
                  onClick={checkout}
                  data-testid="cart-checkout-whatsapp"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-[#25D366]/50 py-3.5 text-xs font-bold uppercase tracking-[0.12em] text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-white"
                >
                  <MessageCircle className="h-4 w-4" />
                  Order on WhatsApp instead
                </button>
                <button
                  onClick={clear}
                  data-testid="cart-clear-button"
                  className="mt-3 w-full text-center text-[11px] uppercase tracking-[0.15em] text-white/35 transition-colors hover:text-white/70"
                >
                  Clear cart
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
